/**
 * スマホ向け操作。左スティックと右ジャンプ、それ以外のドラッグで視点。
 * ボタン上のポインタは視点に使わない。
 */

import type { InputController } from './input';
import { stickIntentFromOffset } from './touch-play';

function isMobileControl(target: EventTarget | null): boolean {
  return target instanceof Element && Boolean(target.closest('[data-mobile-control]'));
}

function capturePointer(node: HTMLElement, pointerId: number): void {
  try {
    node.setPointerCapture(pointerId);
  } catch {
    // dispatchEvent や一部ブラウザではアクティブなポインタがなく、ここで止めると入力が消える
  }
}

export class MobileControls {
  private readonly knob: HTMLElement;
  private stickPointer: number | null = null;
  private jumpPointer: number | null = null;
  private lookPointer: number | null = null;
  private lookX = 0;
  private lookY = 0;

  constructor(
    private readonly canvas: HTMLCanvasElement,
    private readonly input: InputController,
    private readonly stick: HTMLElement,
    private readonly jump: HTMLElement
  ) {
    const knob = stick.querySelector<HTMLElement>('.mobile-stick-knob');
    if (!knob) throw new Error('Missing .mobile-stick-knob');
    this.knob = knob;
    this.attach();
  }

  private attach(): void {
    this.stick.addEventListener('pointerdown', (event) => this.onStickDown(event));
    // 指が円の外で離れても届くよう、終わりと移動は window で見る
    window.addEventListener('pointermove', (event) => this.onStickMove(event));
    window.addEventListener('pointerup', (event) => this.onStickUp(event));
    window.addEventListener('pointercancel', (event) => this.onStickUp(event));
    this.stick.addEventListener('lostpointercapture', (event) => this.onStickUp(event));

    this.jump.addEventListener('pointerdown', (event) => this.onJumpDown(event));
    this.jump.addEventListener('pointerup', (event) => this.onJumpUp(event));
    this.jump.addEventListener('pointercancel', (event) => this.onJumpUp(event));

    this.canvas.addEventListener('pointerdown', (event) => this.onLookDown(event));
    this.canvas.addEventListener('pointermove', (event) => this.onLookMove(event));
    this.canvas.addEventListener('pointerup', (event) => this.onLookUp(event));
    this.canvas.addEventListener('pointercancel', (event) => this.onLookUp(event));

    for (const node of [this.stick, this.jump]) {
      node.addEventListener('contextmenu', (event) => event.preventDefault());
    }
  }

  private onStickDown(event: PointerEvent): void {
    if (!this.input.touchPlaying || this.stickPointer !== null) return;
    event.preventDefault();
    this.stickPointer = event.pointerId;
    capturePointer(this.stick, event.pointerId);
    this.applyStick(event);
  }

  private onStickMove(event: PointerEvent): void {
    if (event.pointerId !== this.stickPointer) return;
    event.preventDefault();
    this.applyStick(event);
  }

  private onStickUp(event: PointerEvent): void {
    if (event.pointerId !== this.stickPointer) return;
    this.stickPointer = null;
    this.input.setVirtualMove(0, 0);
    this.paintStick(0, 0, false);
  }

  private applyStick(event: PointerEvent): void {
    const box = this.stick.getBoundingClientRect();
    const radius = Math.max(1, box.width / 2 - this.knob.offsetWidth / 2);
    const { forward, strafe, knobX, knobY } = stickIntentFromOffset(
      event.clientX - (box.left + box.width / 2),
      event.clientY - (box.top + box.height / 2),
      radius
    );
    this.input.setVirtualMove(forward, strafe);
    this.paintStick(knobX, knobY, forward !== 0 || strafe !== 0);
  }

  private paintStick(knobX: number, knobY: number, active: boolean): void {
    this.knob.style.transform = `translate(${knobX}px, ${knobY}px)`;
    this.stick.classList.toggle('is-active', active);
  }

  private onJumpDown(event: PointerEvent): void {
    if (!this.input.touchPlaying || this.jumpPointer !== null) return;
    event.preventDefault();
    this.jumpPointer = event.pointerId;
    capturePointer(this.jump, event.pointerId);
    this.input.setVirtualJump(true);
    this.jump.classList.add('is-active');
  }

  private onJumpUp(event: PointerEvent): void {
    if (event.pointerId !== this.jumpPointer) return;
    this.jumpPointer = null;
    this.input.setVirtualJump(false);
    this.jump.classList.remove('is-active');
  }

  private onLookDown(event: PointerEvent): void {
    if (!this.input.touchPlaying || this.lookPointer !== null) return;
    if (isMobileControl(event.target)) return;
    event.preventDefault();
    this.lookPointer = event.pointerId;
    this.lookX = event.clientX;
    this.lookY = event.clientY;
    capturePointer(this.canvas, event.pointerId);
  }

  private onLookMove(event: PointerEvent): void {
    if (event.pointerId !== this.lookPointer) return;
    const dx = event.clientX - this.lookX;
    const dy = event.clientY - this.lookY;
    this.lookX = event.clientX;
    this.lookY = event.clientY;
    this.input.addLookPixels(dx, dy);
  }

  private onLookUp(event: PointerEvent): void {
    if (event.pointerId !== this.lookPointer) return;
    this.lookPointer = null;
  }
}
